import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext.jsx';
import {
  fetchPendingQueue,
  approveSubmission,
  rejectSubmission,
} from '../lib/admin.js';
import SubmissionsList from '../components/admin/SubmissionsList.jsx';
import SubmissionDetail from '../components/admin/SubmissionDetail.jsx';
import UsersList from '../components/admin/UsersList.jsx';
import NewsletterAdmin from '../components/admin/NewsletterAdmin.jsx';

export default function Admin() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [tab, setTab] = useState('submissions');
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  async function loadQueue() {
    try {
      setError(null);
      const rows = await fetchPendingQueue();
      setItems(rows);
      // If the currently-selected item is no longer in the queue, clear it.
      if (selected && !rows.some((r) => r.module === selected.module && r.id === selected.id)) {
        setSelected(null);
      }
    } catch (err) {
      setError(err.message ?? String(err));
    }
  }

  useEffect(() => {
    if (tab === 'submissions') loadQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  function showToast(key) {
    setToast(key);
    setTimeout(() => setToast(null), 3500);
  }

  async function handleApprove() {
    if (!selected) return;
    setBusy(true);
    try {
      await approveSubmission({
        module: selected.module,
        id: selected.id,
        reviewerId: user.id,
      });
      showToast('admin.toast.approved');
      setSelected(null);
      await loadQueue();
    } catch (err) {
      setError(err.message ?? String(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleReject({ note }) {
    if (!selected) return;
    setBusy(true);
    try {
      await rejectSubmission({
        module: selected.module,
        id: selected.id,
        reviewerId: user.id,
        note,
      });
      showToast('admin.toast.rejected');
      setSelected(null);
      await loadQueue();
    } catch (err) {
      setError(err.message ?? String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-shell px-6 pb-20 pt-28">
      <header className="mb-8">
        <div className="section-label">{t('nav.admin')}</div>
        <h1 className="font-serif text-4xl font-semibold text-cream">
          {t('admin.heading')}
        </h1>
        <p className="mt-2 text-ink-secondary">{t('admin.subheading')}</p>
      </header>

      {/* Tabs */}
      <div className="mb-8 flex border-b border-white/10">
        <Tab
          active={tab === 'submissions'}
          onClick={() => setTab('submissions')}
          label={t('admin.tabs.submissions')}
          badge={items?.length ?? 0}
        />
        <Tab
          active={tab === 'users'}
          onClick={() => setTab('users')}
          label={t('admin.tabs.users')}
        />
        <Tab
          active={tab === 'newsletter'}
          onClick={() => setTab('newsletter')}
          label={t('admin.tabs.newsletter')}
        />
      </div>

      {error && (
        <p className="mb-6 rounded-md border border-red-500/40 bg-red-950/30 p-3 text-sm text-red-200">
          {error}
        </p>
      )}

      <div role="status" aria-live="polite" className="sr-only sr-only-focusable">
        {toast ? t(toast) : ''}
      </div>
      {toast && (
        <div className="mb-6 rounded-md border border-emerald-500/40 bg-emerald-950/30 p-3 text-sm text-emerald-200">
          {t(toast)}
        </div>
      )}

      {tab === 'submissions' && (
        <div className="grid gap-6 lg:grid-cols-[360px_1fr]">
          <div>
            {items === null ? (
              <p className="rounded-lg border border-white/10 bg-navy-base/30 p-8 text-center text-ink-secondary">
                {t('common.loading')}
              </p>
            ) : (
              <SubmissionsList
                items={items}
                filter={filter}
                onFilterChange={setFilter}
                selectedKey={selected ? `${selected.module}:${selected.id}` : null}
                onSelect={setSelected}
              />
            )}
          </div>
          <div>
            <SubmissionDetail
              item={selected}
              reviewer={user}
              onApprove={handleApprove}
              onReject={handleReject}
              busy={busy}
            />
          </div>
        </div>
      )}

      {tab === 'users' && <UsersList currentUserId={user.id} />}
      {tab === 'newsletter' && <NewsletterAdmin />}
    </div>
  );
}

function Tab({ active, onClick, label, badge }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
        active ? 'text-gold-light' : 'text-ink-secondary hover:text-cream'
      }`}
    >
      {label}
      {badge != null && badge > 0 && (
        <span className="rounded-full border border-gold/40 bg-navy-mid/40 px-2 py-0.5 text-[0.65rem] text-gold-light">
          {badge}
        </span>
      )}
      {active && <span className="absolute inset-x-0 bottom-0 h-0.5 bg-gold-light" />}
    </button>
  );
}
